import React from 'react';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Button, Typography, useThemeValue } from '@altermaind/web/ui';
import { convertSizeToNumber } from '@/_shared/helpers';
import { useBreakingPoints } from '@/_shared/hooks/useBreakpoint';
import { useResponsiveStyle } from '@/_shared/hooks/useResponsiveStyle';
import { INTERNAL_STEPPER } from '@/configuration/constants';
import { getUserProfileCall } from '@/core/state/api/getUserProfileCall';

type Props = {
  setStepper: (step: number) => void;
};

export const StartConfiguration = ({ setStepper }: Props) => {
  const t = useTranslations('configuration.learning.start');
  const { genericResponsiveValue } = useResponsiveStyle();
  const { isDesktop } = useBreakingPoints();
  const { firstName } = getUserProfileCall.hook();

  const gapXXXL = convertSizeToNumber(`${useThemeValue('sizes', 'xxxl')}`) || 64;
  const gapL = convertSizeToNumber(`${useThemeValue('sizes', 'l')}`) || 40;

  return (
    <Box
      display='flex'
      flexDirection={genericResponsiveValue({
        breakPoints: { desktop: 'row' },
        defaultValue: 'column',
      })}
      alignItems='flex-start'
      justifyContent='center'
      px={genericResponsiveValue({
        breakPoints: { desktop: 'xl' },
        defaultValue: 'xxs',
      })}
      py='xl'
      width='100%'
      style={{ gap: isDesktop ? gapXXXL : undefined }}
      flex={10}
    >
      <Box
        flex={genericResponsiveValue({
          breakPoints: { desktop: 4 },
          defaultValue: 10,
        })}
      >
        <Typography
          variant='h2'
          color='primary/text'
          fontSize={genericResponsiveValue({
            breakPoints: { desktop: 50 as never },
            defaultValue: 'xxxl',
          })}
          lineHeight={genericResponsiveValue({
            breakPoints: { desktop: '60px' as never },
            defaultValue: 'xxxl',
          })}
          fontWeight='bold'
          mb={genericResponsiveValue({
            breakPoints: { desktop: 'm' },
            defaultValue: 'xs',
          })}
        >
          {t('title', { name: firstName })}
        </Typography>
        <Typography variant='h4' fontWeight={200} mb='xl' color='primary/text'>
          {t('subtitle')}
        </Typography>
        <Box
          display='flex'
          alignItems='center'
          justifyContent={genericResponsiveValue({
            breakPoints: { desktop: 'flex-start' },
            defaultValue: 'center',
          })}
          style={{ gap: gapL }}
        >
          <Button
            variant='primary'
            spinnerColor='white'
            onClick={() => setStepper(INTERNAL_STEPPER.CONFIGURATION)}
          >
            {t('startConfiguration')}
          </Button>
        </Box>
      </Box>
      <Box
        flex={genericResponsiveValue({
          breakPoints: { desktop: 6 },
          defaultValue: 10,
        })}
      />
    </Box>
  );
};
